"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";

export default function LoginPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [showTotp, setShowTotp] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [siteKey, setSiteKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const captchaRef = useRef<HCaptcha>(null);

  useEffect(() => {
    api.getMe().then(() => router.replace("/apps")).catch(() => {});
    api.getConfig().then((c) => setSiteKey(c.hcaptcha_site_key)).catch(() => {});
  }, [router]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (siteKey && !captchaToken) {
      setError("Please complete the captcha");
      return;
    }

    setLoading(true);
    try {
      const res = await api.login({
        email,
        password,
        captcha_token: captchaToken || undefined,
        totp_code: totpCode || undefined,
      });

      if (res.requires_totp) {
        setShowTotp(true);
        setLoading(false);
        return;
      }

      router.push("/apps");
    } catch (err: any) {
      setError(err.message || "Invalid email or password");
      captchaRef.current?.resetCaptcha();
      setCaptchaToken(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(139,92,246,0.12),transparent)]" />

      <div className="relative z-10 flex w-full max-w-sm flex-col items-center space-y-8 px-6">
        {/* Logo */}
        <Link href="/" className="flex flex-col items-center">
          <Image src="/logo.svg" alt="BuildMagic" width={200} height={59} className="h-12 w-auto" />
        </Link>

        {/* Sign-in card */}
        <div className="w-full rounded-2xl border border-border bg-card p-8">
          <div className="space-y-6">
            <div className="space-y-2 text-center">
              <h2 className="text-xl font-semibold tracking-tight">
                Sign in to your account
              </h2>
              <p className="text-sm text-muted-foreground">
                Build, test, and deliver mobile apps
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              {showTotp && (
                <div className="space-y-2">
                  <Label htmlFor="totp" className="flex items-center gap-2">
                    <ShieldCheck className="size-4 text-green-500" />
                    Two-factor authentication code
                  </Label>
                  <Input
                    id="totp"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    placeholder="Enter 6-digit code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                    autoFocus
                    required
                  />
                </div>
              )}

              {siteKey && (
                <div className="flex justify-center">
                  <HCaptcha
                    ref={captchaRef}
                    sitekey={siteKey}
                    theme="dark"
                    onVerify={(token) => setCaptchaToken(token)}
                    onExpire={() => setCaptchaToken(null)}
                  />
                </div>
              )}

              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}

              <Button type="submit" size="lg" className="w-full" disabled={loading}>
                {loading && <Loader2 className="size-4 animate-spin" />}
                {showTotp ? "Verify & Sign in" : "Sign in"}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground">
              Don&apos;t have an account?{" "}
              <Link href="/register" className="font-medium text-violet-400 hover:text-violet-300 transition-colors">
                Create one
              </Link>
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground/60">
          By signing in, you agree to our{" "}
          <Link href="/terms" className="underline hover:text-muted-foreground">Terms of Service</Link> and{" "}
          <Link href="/privacy" className="underline hover:text-muted-foreground">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
}
